// Answer validation + correct-answer labels — ported from web_claude.
// Answer shapes per type:
//   true_false      -> boolean
//   multiple_choice -> number[]            (selected option indexes)
//   fill_blank      -> string[]            (typed value per blank)
//   word_bank_fill  -> (number|null)[]     (pool index per blank)
//   drag_drop       -> { assignments: number[][] }  (item indexes per slot)
import {
  parseFillBlankData,
  getWordBankCorrectWords,
  getWordBankPool,
  getMultipleChoiceCorrectIndexes,
} from "./parsing";

// ── text normalization + fuzzy matching (fill_blank) ──────────────────────────
// Lenient matching: case-insensitive (tr-TR aware) and Turkish-letter folding —
// ş↔s, ç↔c, ö↔o, ü↔u, ğ↔g, ı↔i — so "olcumsel" matches "ölçümsel" etc.
const TR_FOLD = {
  ş: "s",
  ç: "c",
  ö: "o",
  ü: "u",
  ğ: "g",
  ı: "i",
};

export function normalizeAnswerText(value) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/[şçöüğı]/g, (ch) => TR_FOLD[ch] || ch)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, " ");
}

function levenshteinDistance(a, b) {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  const curr = new Array(b.length + 1).fill(0);

  for (let i = 1; i <= a.length; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j += 1) prev[j] = curr[j];
  }
  return prev[b.length];
}

function isMinorSpellingMatch(candidate, target) {
  const maxLen = Math.max(candidate.length, target.length);
  // No fuzzy tolerance for short answers. A single edit on a 1–4 char answer
  // spans a different answer, not a typo: distance 1 on "y" accepts every other
  // letter (t, k, …) and on "1" accepts every other digit (4, 5, …). Typo
  // forgiveness only kicks in from 5+ chars, where one edit is unambiguous.
  // (Turkish-letter / case leniency is handled separately in normalizeAnswerText.)
  if (maxLen < 5) return false;
  const distance = levenshteinDistance(candidate, target);
  if (maxLen <= 8) return distance <= 1;
  return distance <= 2;
}

export function matchesFillBlankValue(candidateRaw, acceptedRawList) {
  const candidate = normalizeAnswerText(candidateRaw);
  if (!candidate) return false;
  return acceptedRawList.some((accepted) => {
    const normalizedAccepted = normalizeAnswerText(accepted);
    if (!normalizedAccepted) return false;
    if (normalizedAccepted === candidate) return true;
    return isMinorSpellingMatch(candidate, normalizedAccepted);
  });
}

// ── drag_drop helpers ─────────────────────────────────────────────────────────
export function normalizeCorrectBySlot(interaction) {
  const raw = interaction.correctBySlot;
  if (!Array.isArray(raw)) return interaction.slots.map(() => []);
  if (raw.length > 0 && typeof raw[0] === "number") {
    return raw.map((v) => (Number.isInteger(v) ? [v] : []));
  }
  return raw.map((g) => (Array.isArray(g) ? g.filter((v) => Number.isInteger(v)) : []));
}

function setsEqual(a, b) {
  const sa = [...a].sort((x, y) => x - y);
  const sb = [...b].sort((x, y) => x - y);
  return sa.length === sb.length && sa.every((v, i) => v === sb[i]);
}

// ── word_bank blank groups (order-independent blanks) ─────────────────────────
function multisetsEqual(a, b) {
  if (a.length !== b.length) return false;
  const sa = [...a].sort();
  const sb = [...b].sort();
  return sa.every((v, i) => v === sb[i]);
}

// Sanitize interaction.blankGroups into valid, deduped groups of 2+ blank
// indexes. Each blank index belongs to at most one group.
export function normalizeBlankGroups(interaction, blankCount) {
  const raw = interaction.blankGroups;
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const groups = [];
  raw.forEach((group) => {
    if (!Array.isArray(group)) return;
    const g = [];
    group.forEach((i) => {
      if (Number.isInteger(i) && i >= 0 && i < blankCount && !seen.has(i)) {
        seen.add(i);
        g.push(i);
      }
    });
    if (g.length >= 2) groups.push(g);
  });
  return groups;
}

// Per-blank correctness for word_bank, group-aware. Grouped blanks are all
// correct when the group's placed words match its expected words as a multiset
// (any order); ungrouped blanks match positionally. Used for both validation
// and the per-blank ✓/✕ lighting so they never disagree.
export function wordBankBlankCorrectness(interaction, assignments) {
  const expected = getWordBankCorrectWords(interaction);
  const pool = getWordBankPool(interaction);
  const placed = (assignments || []).map((idx) =>
    idx === null || idx === undefined ? null : pool[idx]
  );
  const groups = normalizeBlankGroups(interaction, expected.length);
  const groupOfBlank = new Array(expected.length).fill(-1);
  groups.forEach((g, gi) => g.forEach((i) => (groupOfBlank[i] = gi)));
  const groupOk = groups.map((g) =>
    multisetsEqual(g.map((i) => placed[i]), g.map((i) => expected[i]))
  );
  return expected.map((word, i) => {
    if (placed[i] === null || placed[i] === undefined) return false;
    if (groupOfBlank[i] >= 0) return groupOk[groupOfBlank[i]];
    return placed[i] === word;
  });
}

// ── validation ────────────────────────────────────────────────────────────────
export function validateAnswer(interaction, answer) {
  switch (interaction.type) {
    case "true_false":
      return Boolean(answer) === interaction.correct;

    case "multiple_choice": {
      const correct = getMultipleChoiceCorrectIndexes(interaction);
      const selected = Array.from(new Set(answer || []));
      if (interaction.selectionMode === "single") {
        return selected.length === 1 && correct.length >= 1 && correct[0] === selected[0];
      }
      return setsEqual(correct, selected);
    }

    case "fill_blank": {
      const { blanks } = parseFillBlankData(interaction);
      const values = answer || [];
      if (values.length !== blanks.length) return false;
      return blanks.every((accepted, i) => matchesFillBlankValue(values[i] ?? "", accepted));
    }

    case "word_bank_fill": {
      const expected = getWordBankCorrectWords(interaction);
      const assignments = answer || [];
      if (assignments.length !== expected.length) return false;
      if (assignments.some((idx) => idx === null || idx === undefined)) return false;
      return wordBankBlankCorrectness(interaction, assignments).every(Boolean);
    }

    case "drag_drop": {
      const correctBySlot = normalizeCorrectBySlot(interaction);
      const a = answer;
      if (!a || !Array.isArray(a.assignments)) return false;
      if (a.assignments.length !== correctBySlot.length) return false;
      return a.assignments.every((group, idx) => setsEqual(group, correctBySlot[idx] || []));
    }

    case "info_hint":
      return true;

    default:
      return false;
  }
}

// ── reveal labels ("Cevabı Göster") ─────────────────────────────────────────────
export function correctAnswerLabel(interaction) {
  switch (interaction.type) {
    case "true_false":
      return interaction.correct ? "Doğru" : "Yanlış";

    case "multiple_choice":
      return interaction.options
        .filter((o) => o.isCorrect)
        .map((o) => o.text)
        .join(", ");

    case "fill_blank": {
      const { blanks } = parseFillBlankData(interaction);
      return blanks.map((answers) => answers[0] ?? "").join(", ");
    }

    case "word_bank_fill":
      return getWordBankCorrectWords(interaction).join(", ");

    case "drag_drop": {
      const correctBySlot = normalizeCorrectBySlot(interaction);
      return interaction.slots
        .map((slot, idx) => {
          const labels = (correctBySlot[idx] || [])
            .map((i) => interaction.items[i])
            .filter(Boolean)
            .join(", ");
          return `${slot}: ${labels}`;
        })
        .join("   |   ");
    }

    default:
      return "";
  }
}
